/**
 * ImageField — an `image` attribute: uploads a single image and stores its
 * upload id as the field value (PIM Wave P2, Task 3). Low priority per spec §8
 * Q3.
 *
 * Reuses `usePhotoUpload` (sign → S3 → poll-to-clean); the backend validates an
 * `image` attribute value by upload existence + ownership + clean scan, NOT by
 * purpose, so the `product-photo` purpose is reused (the plan named
 * `product-attribute`, which is NOT in the closed `UPLOAD_PURPOSES` set —
 * flagged). The stored value is the upload UUID (`AttributeValueOut.value` for
 * an image is the bare id — there is no servable URL on the wire), so on an
 * edit-mode reload only an "attached" chip is shown; a freshly-uploaded image
 * shows its local preview for the rest of the session.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@garden/ui";

import { SELLER_DASHBOARD_NS } from "@/features/seller-dashboard/api";
import {
  isPresent,
  pickOptionalText,
  pickText,
} from "@/features/seller-dashboard/attributes/formSchema";
import {
  FieldShell,
  type AttributeFieldProps,
} from "@/features/seller-dashboard/attributes/fields/FieldShell";
import { usePhotoUpload } from "@/features/seller-dashboard/usePhotoUpload";

export function ImageField({
  field,
  language,
  value,
  onChange,
  error,
}: AttributeFieldProps): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const label = pickText(field.label, language);
  const hint = pickOptionalText(field.hint, language);
  const { drafts, add, remove } = usePhotoUpload();
  const promoted = useRef<Set<string>>(new Set());
  // Local preview for an image uploaded THIS session (the wire value is only
  // the upload id — no URL — so an edit-mode reload has no preview).
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    for (const d of drafts) {
      if (
        d.state === "ready" &&
        d.uploadId !== null &&
        !promoted.current.has(d.localId)
      ) {
        promoted.current.add(d.localId);
        onChange(d.uploadId);
        setPreview(d.previewUrl);
        remove(d.localId);
      }
    }
  }, [drafts, onChange, remove]);

  const handleFiles = (files: FileList | null): void => {
    if (files === null || files.length === 0) return;
    void add(files[0] as File);
  };

  const handleRemove = (): void => {
    onChange("");
    setPreview(null);
  };

  const inFlight = drafts.filter((d) => d.state !== "ready");
  const hasValue = isPresent(value);

  return (
    <FieldShell
      slug={field.field_slug}
      label={label}
      requirement={field.requirement}
      hint={hint}
      error={error}
    >
      {hasValue ? (
        <div
          className="flex items-center gap-3 rounded-[var(--g-radius)] border border-border bg-card px-3 py-2"
          data-testid={`attr-widget-${field.field_slug}`}
        >
          {preview ? (
            <img
              src={preview}
              alt=""
              className="size-12 rounded-[var(--g-radius)] object-cover"
            />
          ) : (
            <div className="flex size-12 items-center justify-center rounded-[var(--g-radius)] bg-muted text-muted-foreground">
              <Icon name="camera" size={18} />
            </div>
          )}
          <span className="flex-1 text-sm text-muted-foreground">
            {t("attributes.image.attached")}
          </span>
          <button
            type="button"
            onClick={handleRemove}
            aria-label={t("attributes.image.remove")}
            className="inline-flex size-11 items-center justify-center rounded-[var(--g-radius)] text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      ) : (
        <label
          className="flex min-h-[88px] cursor-pointer flex-col items-center justify-center gap-1 rounded-[var(--g-radius)] border-2 border-dashed border-border bg-card p-4 text-center focus-within:ring-2 focus-within:ring-ring"
          data-testid={`attr-widget-${field.field_slug}`}
        >
          <Icon name="camera" size={20} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {t("attributes.image.drop")}
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            aria-label={label}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </label>
      )}

      {inFlight.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {inFlight.map((d) => (
            <li
              key={d.localId}
              className={[
                "text-xs",
                d.state === "error" ? "text-[var(--g-error)]" : "text-muted-foreground",
              ].join(" ")}
              role={d.state === "error" ? "alert" : "status"}
            >
              {d.state === "uploading"
                ? t("photos.uploading")
                : d.state === "scanning"
                  ? t("photos.scanning")
                  : d.errorCode
                    ? t(`photos.${d.errorCode}`)
                    : t("photos.uploadFailed")}
            </li>
          ))}
        </ul>
      ) : null}
    </FieldShell>
  );
}

export default ImageField;
